//
// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React, {useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import CheckBox from "@material-ui/core/CheckBox";
import CloseableDialog from "eai-configurator-ui/components/configuration/dialog/CloseableDialog";
import {TextValidator, ValidatorForm} from "react-material-ui-form-validator";
import * as Utils from "eai-configurator-ui/components/Utils";
import MessageDialog, {useMessageDialog,} from "eai-configurator-ui/components/configuration/dialog/MessageDialog";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import AsyncComboBox from "eai-configurator-ui/components/base/AsyncComboBox";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Portal from "@material-ui/core/Portal";
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  formControlLabel: {
    width: "100%"
  },
  textField: {
    width: "100%",
  },
  comboBox: {
    paddingTop: 10,
  },
  error: {
    color: "red",
  },
  checkbox: {
    paddingLeft: 0,
  },
}));

const AutomateDatabaseEditDialog = ({
                                      open,
                                      handleSuccess,
                                      handleClose,
                                      inputAutomateDatabase,
                                    }) => {
  const classes = useStyles();
  const [automateDatabase, setAutomateDatabase] = useState({});
  const [automateLoginSettings, setAutomateLoginSettings] = useState({samlLogin: false, hasSamlToken: false, downloadTokenUrl: null});
  const [alertMessage, setAlertMessage] = React.useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useMessageDialog();
  const [isError, setIsError] = React.useState(false);

  React.useEffect(() => {
    let automateDatabase = inputAutomateDatabase;
    if (!automateDatabase) {
      automateDatabase = {};
    }
    setAutomateDatabase({...automateDatabase});
  }, [inputAutomateDatabase]);

  React.useEffect(() => {
    populateLoginSettings();
  }, []);

  const populateLoginSettings = () => {
    Utils.fetchData("loader/automateLoginSettings")
      .then((data) => {
        setAutomateLoginSettings(data);
      })
      .catch(function (response) {
        Utils.getErrorMessage(response, "Failed to fetch login settings.").then(
          (message) =>
            setAlertMessage({
              success: false,
              message: message,
            })
        );
      });
  };

  const setAutomateDatabaseProperty = (event, propertyName) => {
    const automateDatabaseClone = {...automateDatabase};
    automateDatabaseClone[propertyName] = event.target.value;
    setAutomateDatabase(automateDatabaseClone);
  };

  const setAutomateDatabasePropertyFromCheckbox = (event, propertyName) => {
    const automateDatabaseClone = {...automateDatabase};
    automateDatabaseClone[propertyName] = event.target.checked;
    setAutomateDatabase(automateDatabaseClone);
  };

  const setAutomateDatabasePropertyFromValue = (value, propertyName) => {
    const automateDatabaseClone = {...automateDatabase};
    automateDatabaseClone[propertyName] = value;
    setAutomateDatabase(automateDatabaseClone);
  };

  const setSelectedProfilePropertyFromValue = (value, propertyName) => {
    const automateDatabaseClone = {...automateDatabase};
    automateDatabaseClone[propertyName] = value;
    setAutomateDatabase(automateDatabaseClone);
  };

  const setSamlTokenContents = (tokenContents, username) => {
    const automateDatabaseClone = {...automateDatabase};
    automateDatabaseClone.username = username;
    automateDatabaseClone.samlTokenContents = tokenContents;
    setAutomateDatabase(automateDatabaseClone);
  };

  const setSelectedProfileName = (value) => {
    const useValue = value === "NONE" ? "" : value;
    setSelectedProfilePropertyFromValue(useValue, "selectedProfileName");
  };
  const setDescription = (event) => {
    setAutomateDatabaseProperty(event, "description");
  };
  const setAutomateDatabaseCheckbox = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "automate");
  };
  const setUpdateOnUserLogin = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "updateOnUserLogin");
  };
  const setExportDailyByDefault = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportDailyByDefault");
  };
  const setExportDailyWhatIf = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportDailyWhatIf");
  };
  const setExportDailyActive = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportDailyActive");
  };
  const setExportDailyInactive = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportDailyInactive");
  };
  const setExportDailyPlanned = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportDailyPlanned");
  };
  const setExportDailyTemplate = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportDailyTemplate");
  };

  const setExportWeeklyByDefault = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportWeeklyByDefault");
  };
  const setExportWeeklyDayOfWeek = (event) => {
    setAutomateDatabaseProperty(event, "exportWeeklyDayOfWeek");
  };
  const setExportWeeklyWhatIf = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportWeeklyWhatIf");
  };
  const setExportWeeklyActive = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportWeeklyActive");
  };
  const setExportWeeklyInactive = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportWeeklyInactive");
  };
  const setExportWeeklyPlanned = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportWeeklyPlanned");
  };
  const setExportWeeklyTemplate = (event) => {
    setAutomateDatabasePropertyFromCheckbox(event, "exportWeeklyTemplate");
  };

  const setUsername = (event) => {
    if (!automateLoginSettings.samlLogin) {
      setAutomateDatabaseProperty(event, "username");
    }
  };
  const setPassword = (event) => {
    setAutomateDatabaseProperty(event, "password");
  };
  const isEditingExistingAutomateDatabase = () => {
    return inputAutomateDatabase;
  };

  const [isTokenUploadPending, setTokenUploadPending] = useState(false);

  //-----------------------------------
  // SAML token related functions
  //-----------------------------------

  /**
   * Shows a popup window where the user can download the SAML assertion token from.
   */
  const getToken = () => {
    window.open(automateLoginSettings.downloadTokenUrl)
  }

  /**
   * Shows the dialog to upload the token file to the server.
   */
  const uploadSamlAssertionTokenFile = () => {
    const fileSelect = document.getElementById("contained-button-file")
    fileSelect.click()
  }

  /**
   * Executed when the user actually picks a SAML assertion token file. This is what uploads it
   *  to the server.
   */
  const uploadSamlAssertionTokenFileAfterSelection = (e) => {
    console.log("Will upload saml token file");

    const fileSelect = document.getElementById("contained-button-file");
    const selectedFile = fileSelect.files[0];

    // The token can only be uploaded to the Cloud API URL
    uploadTokenToServer(selectedFile, "loader/automateUploadSamlToken")
  }

  /**
   * Upload the SAML token to the server. The response will be the username, the token, and possibly some extra information,
   *  or an error message in case something goes wrong.
   */
  const uploadTokenToServer = (file, uri) => {
    console.log("Submitting file [" + file + "] to URL [" + uri + "]");
    setTokenUploadPending(true);
    const data = new FormData();
    data.append("file", file);

    Utils.fetchData(
      uri,
      {
        method: "POST",
        body: data,
      },
      false,
      true
    ).then((tokenContents) => {
      setTokenUploadPending(false)
      console.log("Response received from server");
      console.table(tokenContents);

      //
      // Process the response contents
      processTokenResponse(tokenContents);
    })
      .catch(handleUploadTokenError);
  };

  /**
   * Process a token response received from the server after a token has been received
   */
  const processTokenResponse = (tokenContents) => {
    // Sets the new login state with the properties received from the server
    setSamlTokenContents(tokenContents.token, tokenContents.username)
  }

  /**
   * Invoked when the token upload fails for whichever reason.
   */
  const handleUploadTokenError = (response) => {
    console.log("Upload token error: " + response);
    console.table(response);
    setTokenUploadPending(false)
    setAlertMessage(response);
  };

  //-----------------------------------
  // ENDOF SAML token related functions
  //-----------------------------------

  const handleApply = () => {
    setSubmitted(true);
    const params = {
      id: automateDatabase.id || -1,
      instanceNumber: automateDatabase.instanceNumber || 0,
      instanceName: automateDatabase.instanceName || "",
      description: automateDatabase.description,
      automate: automateDatabase.automate || false,
      loginDatabaseId: automateDatabase.loginDatabaseOption.id || 0,
      username: automateDatabase.username || "",
      password: automateDatabase.password,
      samlTokenContents: automateDatabase.samlTokenContents,
      updateOnUserLogin: automateDatabase.updateOnUserLogin || false,
      exportDailyByDefault: automateDatabase.exportDailyByDefault || false,
      exportDailyWhatIf: automateDatabase.exportDailyWhatIf || false,
      exportDailyActive: automateDatabase.exportDailyActive || false,
      exportDailyInactive: automateDatabase.exportDailyInactive || false,
      exportDailyPlanned: automateDatabase.exportDailyPlanned || false,
      exportDailyTemplate: automateDatabase.exportDailyTemplate || false,
      exportWeeklyByDefault: automateDatabase.exportWeeklyByDefault || false,
      exportWeeklyDayOfWeek: automateDatabase.exportWeeklyDayOfWeek,
      exportWeeklyWhatIf: automateDatabase.exportWeeklyWhatIf || false,
      exportWeeklyActive: automateDatabase.exportWeeklyActive || false,
      exportWeeklyInactive: automateDatabase.exportWeeklyInactive || false,
      exportWeeklyPlanned: automateDatabase.exportWeeklyPlanned || false,
      exportWeeklyTemplate: automateDatabase.exportWeeklyTemplate || false,
      selectedProfileName: automateDatabase.selectedProfileName
    };
    if (inputAutomateDatabase) {
      params["id"] = inputAutomateDatabase.id;
    }
    console.log("Submit params:");
    console.table(params);
    const submitBody = Utils.createFormBody(params)
    console.log("Submit body:");
    console.table(submitBody);
    Utils.fetchData(
      "loader/saveAutomateDatabase",
      {
        method: "post",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: submitBody,
      },
      false,
      false
    )
      .catch((e) => {
        setMessage("Database edit failed");
        setIsError(true);
        setSubmitted(false);
      })
      .then((response) => {
        if (response && response.ok) {
          handleClose();
          setSubmitted(false);
          handleSuccess(automateDatabase);
        } else {
          setMessage("Database edit failed");
          setIsError(true);
          setSubmitted(false);
        }
      });
  };

  const isEditing = isEditingExistingAutomateDatabase();
  return (
    <div>
      <MessageDialog {...message} error={isError} success={!isError}/>
      <CloseableDialog
        title={isEditing ? "Edit Automate Database" : "Add Automate Database"}
        open={open}
        maxWidth="md"
        fullWidth
        handleClose={handleClose}
      >
        <ValidatorForm onSubmit={handleApply} style={{width: "100%"}}>
          <DialogContent dividers>
            {alertMessage && (
              <Portal>
                <Snackbar
                  open={alertMessage}
                  autoHideDuration={6000}
                  onClose={() => setAlertMessage(null)}
                >
                  <Alert
                    onClose={() => setAlertMessage(null)}
                    severity={alertMessage && alertMessage.success ? "success" : "error"}
                  >
                    {alertMessage && (alertMessage.message || "Unknown error")}
                  </Alert>
                </Snackbar>
              </Portal>
            )}

            <TextValidator
              className={classes.textField}
              label="Database Information"
              type="text"
              variant="standard"
              readonly={true}
              // disabled={true
              value={automateDatabase.instanceNumber + "/ " + automateDatabase.instanceName || ""}
              id="databaseInfo"
            />
            <hr/>
            <h6>Basic Options</h6>
            <TextValidator
              className={classes.textField}
              label="Description"
              type="text"
              variant="standard"
              value={automateDatabase.description || ""}
              onChange={setDescription}
              id="username"
            />
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <CheckBox
                  label="Automate"
                  variant="standard"
                  checked={automateDatabase.automate || false}
                  onClick={setAutomateDatabaseCheckbox}
                />
              }
              label="Automate the generation on this database with these credentials:"/>
            <TextValidator
              className={classes.textField}
              label="Username"
              type="text"
              variant="standard"
              disabled={automateLoginSettings.samlLogin}
              value={automateDatabase.username || ""}
              onChange={setUsername}
              validators={["required"]}
              id="username"
            />
            {
              !automateLoginSettings.samlLogin &&
              <TextValidator
                className={classes.textField}
                label="Password"
                type="password"
                variant="standard"
                autoComplete="new-password"
                value={automateDatabase.password || ""}
                onChange={setPassword}
                id="username"
              />
            }
            {
              automateLoginSettings.samlLogin &&
              <Grid container spacing={1} xs={12}>
                <Grid item xs={12}>
                  <Box display="flex" flexDirection="row">
                    <Typography variant="caption" >
                      This application is using SSO/ SAML authentication. Here you can download/ upload the token
                      for the SAML authentication. This token will be used in the automation process. You can select the option
                      to update the stored credentials upon login - every time this user logs in, the saved token will be updated with
                      the token uploaded by the user.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    className={classes.submit}
                    fullWidth
                    onClick={getToken}
                  >
                    Get Token
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <input
                    type="file"
                    accept="text/xml"
                    style={{display: 'none'}}
                    onChange={uploadSamlAssertionTokenFileAfterSelection}
                    id="contained-button-file"
                  />
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    className={classes.submit}
                    fullWidth
                    onClick={uploadSamlAssertionTokenFile}
                    disabled={isTokenUploadPending}
                  >
                    Upload Token
                    {isTokenUploadPending && (
                      <CircularProgress
                        color="inherit"
                        style={{marginLeft: "10px"}}
                        size={20}
                      />
                    )}
                  </Button>
                </Grid>
              </Grid>
            }
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <CheckBox
                  label="Update On User Login"
                  variant="standard"
                  checked={automateDatabase.updateOnUserLogin || false}
                  onClick={setUpdateOnUserLogin}
                />
              }
              label="Update stored credentials when this user logs in"/>
            <hr/>
            <h6>Daily Export</h6>
            <Typography variant="caption" color="textSecondary">
              Use this option to mark all projects in this database to be exported by default on a daily basis. If you don't
              want a project to be exported from this database, assign the SKIP value to the daily export code. Changing
              the value of this code <b>won't affect</b> projects that have already been marked to be exported daily.
              You need to remove the code from these projects manually. The status filter is always applied.
            </Typography>
            <FormControlLabel
              className={classes.formControlLabel}
              control={
                <CheckBox
                  label="Export Daily By Default"
                  variant="standard"
                  checked={automateDatabase.exportDailyByDefault || false}
                  onClick={setExportDailyByDefault}
                />
              }
              label="Export daily by default"/>
            For these statuses:
            <div>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportDailyActive || false}
                      onClick={setExportDailyActive}
                    />
                  </>
                }
                label="Active"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportDailyPlanned || false}
                      onClick={setExportDailyPlanned}
                    />
                  </>
                }
                label="Planned"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportDailyWhatIf || false}
                      onClick={setExportDailyWhatIf}
                    />
                  </>
                }
                label="What-If"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportDailyInactive || false}
                      onClick={setExportDailyInactive}
                    />
                  </>
                }
                label="Inactive"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportDailyTemplate || false}
                      onClick={setExportDailyTemplate}
                    />
                  </>
                }
                label="Template"/>
            </div>

            <hr/>
            <h6>Weekly Export</h6>
            <Typography variant="caption" color="textSecondary">
              Use this option to mark all projects in this database to be exported by default on a weekly basis. If you don't
              want a project to be exported from this database, assign the SKIP value to the weekly export code. Changing
              the value of this code <b>won't affect</b> projects that have already been marked to be exported weekly. You need to
              remove the code from these projects manually. The status filter is always applied.
            </Typography>
            <div>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportWeeklyByDefault || false}
                      onClick={setExportWeeklyByDefault}
                    />
                  </>
                }
                label="Export weekly by default on a"/>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={automateDatabase.exportWeeklyDayOfWeek || "SUNDAY"}
                disabled={automateDatabase.exportWeeklyByDefault === false}
                label="Day of Week"
                onChange={setExportWeeklyDayOfWeek}
              >
                <MenuItem value={"SUNDAY"}>SUNDAY</MenuItem>
                <MenuItem value={"MONDAY"}>MONDAY</MenuItem>
                <MenuItem value={"TUESDAY"}>TUESDAY</MenuItem>
                <MenuItem value={"WEDNESDAY"}>WEDNESDAY</MenuItem>
                <MenuItem value={"THURSDAY"}>THURSDAY</MenuItem>
                <MenuItem value={"FRIDAY"}>FRIDAY</MenuItem>
                <MenuItem value={"SATURDAY"}>SATURDAY</MenuItem>
              </Select>
            </div>
            For these statuses:
            <div>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportWeeklyActive || false}
                      onClick={setExportWeeklyActive}
                    />
                  </>
                }
                label="Active"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportWeeklyPlanned || false}
                      onClick={setExportWeeklyPlanned}
                    />
                  </>
                }
                label="Planned"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportWeeklyWhatIf || false}
                      onClick={setExportWeeklyWhatIf}
                    />
                  </>
                }
                label="What-If"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportWeeklyInactive || false}
                      onClick={setExportWeeklyInactive}
                    />
                  </>
                }
                label="Inactive"/>
              <FormControlLabel
                control={
                  <>
                    <CheckBox
                      variant="standard"
                      checked={automateDatabase.exportWeeklyTemplate || false}
                      onClick={setExportWeeklyTemplate}
                    />
                  </>
                }
                label="Template"/>
            </div>

            <hr/>
            <h6>Configuration Profile</h6>
            <Typography variant="caption" color="textSecondary">
              The list of global and project modules extracted in the automation is defined in the menu option 'Modules', and
              is saved as part of a configuration profile. Only global profiles may be used. The configuration profile used by the automation is defined below.
            </Typography>
            <AsyncComboBox
              key={"configuration-profile"}
              url="configuration/profileList?globalOnly=true&includeNoneOption=NONE"
              textFieldProps={{fullWidth: true, label: "Configuration Profile"}}
              onChange={(event, value) => setSelectedProfileName(value)}
              value={automateDatabase.selectedProfileName}
              style={{width: "100%"}}
              autoSelect={true}
            />
          </DialogContent>
          <DialogActions>
            <Button type="submit" color="primary" disabled={submitted}>
              {submitted ? "Applying..." : "Apply"}
            </Button>
          </DialogActions>
        </ValidatorForm>
      </CloseableDialog>
    </div>
  );
};
export default AutomateDatabaseEditDialog;
